"use client";

import React, { useState } from "react";
import { Button } from "@/components/ui/button";
import { ScrollArea } from "@/components/ui/scroll-area";
import { Badge } from "@/components/ui/badge";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { Input } from "@/components/ui/input";
import {
  Bell,
  Briefcase,
  Calendar,
  DollarSign,
  FileText,
  Settings,
  Check,
  X,
  Search,
  ChevronRight,
  Inbox,
  MessageSquare,
  AlarmClock
} from "lucide-react";
import { formatDistanceToNow } from "date-fns";
import { useNotifications } from "@/hooks/use-notifications";
import { useAuth } from "@/contexts/AuthContext";
import { cn } from "@/lib/utils";
import { useNavigate } from "react-router-dom";
import { motion, AnimatePresence } from "framer-motion";
import { useMediaQuery } from "@/hooks/use-media-query";
import { inferTargetRole, getNotificationNavigationUrl } from "@/utils/notificationNavigation";

interface Notification {
  id: string;
  type: string;
  title: string;
  message: string;
  data?: any;
  read: boolean;
  read_at?: string;
  created_at: string;
}

const categoryIcons = {
  job: Briefcase,
  booking: Calendar,
  pitch: FileText,
  payment: DollarSign,
  chat: MessageSquare,
  calendar: AlarmClock,
  system: Settings,
};

const categoryColors = {
  job: "text-blue-600 bg-blue-50",
  booking: "text-green-600 bg-green-50",
  pitch: "text-purple-600 bg-purple-50",
  payment: "text-yellow-600 bg-yellow-50",
  chat: "text-indigo-600 bg-indigo-50",
  calendar: "text-violet-600 bg-violet-50",
  system: "text-gray-600 bg-gray-50",
};

// Helper function to get category from notification type
const getCategoryFromType = (type: string): 'job' | 'booking' | 'pitch' | 'payment' | 'chat' | 'calendar' | 'system' => {
  if (type.includes('message') || type.includes('chat')) return 'chat';
  if (type === 'calendar_reminder') return 'calendar'; // Calendar reminders get their own category
  if (type.includes('booking')) return 'booking';
  if (type.includes('pitch') || type.includes('deck') || type.includes('nda')) return 'pitch';
  if (type.includes('job') || type.includes('application') || type.includes('contract')) return 'job';
  if (type.includes('payment') || type.includes('payout')) return 'payment';
  if (type.includes('deliverable')) return 'job'; // Deliverables are job-related
  return 'system';
};

interface NotificationDropdownProps {
  isOpen: boolean;
  onClose: () => void;
}

export function NotificationDropdown({ isOpen, onClose }: NotificationDropdownProps) {
  const [searchQuery, setSearchQuery] = useState("");
  const [selectedCategory, setSelectedCategory] = useState<string>("all");
  const navigate = useNavigate();
  const isMobile = useMediaQuery('(max-width: 768px)');
  const { userType } = useAuth();

  const {
    notifications,
    unreadCount,
    isLoading,
    markAsRead,
    markAllAsRead,
    deleteNotification,
    loadMore,
    hasMore,
  } = useNotifications();

  // Get the current dashboard from URL
  const currentDashboard = window.location.pathname.includes('/client') ? 'client' : 'filmmaker';

  // Calculate unread counts per category
  const unreadCounts = {
    all: notifications.filter(n => !n.read).length,
    job: notifications.filter(n => !n.read && getCategoryFromType(n.type) === 'job').length,
    booking: notifications.filter(n => !n.read && getCategoryFromType(n.type) === 'booking').length,
    pitch: notifications.filter(n => !n.read && getCategoryFromType(n.type) === 'pitch').length,
    chat: notifications.filter(n => !n.read && getCategoryFromType(n.type) === 'chat').length,
    payment: notifications.filter(n => !n.read && getCategoryFromType(n.type) === 'payment').length,
    calendar: notifications.filter(n => !n.read && getCategoryFromType(n.type) === 'calendar').length,
    system: notifications.filter(n => !n.read && getCategoryFromType(n.type) === 'system').length,
  };

  // Filter notifications based on search and category
  const filteredNotifications = notifications.filter((notif) => {
    const matchesSearch = 
      notif.title.toLowerCase().includes(searchQuery.toLowerCase()) ||
      notif.message.toLowerCase().includes(searchQuery.toLowerCase());
    
    const category = getCategoryFromType(notif.type);
    const matchesCategory = 
      selectedCategory === "all" || category === selectedCategory;
    
    return matchesSearch && matchesCategory;
  });

  const handleNotificationClick = async (notification: Notification) => {
    // Mark as read if unread
    if (!notification.read) {
      await markAsRead([notification.id]);
    }

    // External link short-circuit (e.g. external job notification → external job detail in-app)
    const actionUrl = (notification.data as { action_url?: string; external_job_id?: string } | null | undefined)?.action_url;
    const externalJobId = (notification.data as { external_job_id?: string } | null | undefined)?.external_job_id;
    if (externalJobId) {
      onClose();
      navigate(`/filmmaker/dashboard/jobs?tab=external&externalJob=${externalJobId}`);
      return;
    }
    if (actionUrl && actionUrl.startsWith('http')) {
      onClose();
      window.open(actionUrl, '_blank', 'noopener,noreferrer');
      return;
    }

    // Determine the target role for this notification
    let targetRole: 'filmmaker' | 'client' = currentDashboard;

    if (userType === 'both') {
      // For dual-account users, infer the correct role from notification type/context
      const inferredRole = inferTargetRole({
        type: notification.type,
        data: notification.data,
      });

      if (inferredRole) {
        targetRole = inferredRole;
      }
    } else if (userType === 'filmmaker' || userType === 'client') {
      // For single-role users, use their role
      targetRole = userType;
    }

    // Get the navigation URL using the target role
    const url = getNotificationNavigationUrl(
      { type: notification.type, data: notification.data },
      targetRole
    );

    if (url) {
      onClose();
      navigate(url);
    }
  };

  const NotificationItem = ({ notification }: { notification: Notification }) => {
    const category = getCategoryFromType(notification.type);
    const Icon = categoryIcons[category];
    const colorClass = categoryColors[category];
    
    return (
      <motion.div
        initial={{ x: -20, opacity: 0 }}
        animate={{ x: 0, opacity: 1 }}
        exit={{ x: 20, opacity: 0 }}
        className={cn(
          "group flex items-start gap-3 p-3 rounded-lg cursor-pointer outline-none focus:outline-none transition-colors max-w-full",
          !notification.read ? "bg-accent/30" : "hover:bg-accent/50"
        )}
        onClick={() => handleNotificationClick(notification)}
        whileHover={{ scale: 1.01 }}
        whileTap={{ scale: 0.99 }}
      >
        <div className={cn("p-2 rounded-full", colorClass)}>
          <Icon className="h-4 w-4" />
        </div>
        
        <div className="flex-1 min-w-0">
          <div className="flex items-center justify-between mb-1">
            <p className={cn(
              "text-sm font-medium",
              !notification.read && "font-semibold"
            )}>
              {notification.title}
            </p>
            {!notification.read && (
              <div className="h-2 w-2 bg-primary rounded-full" />
            )}
          </div>
          
          <p className="text-sm text-muted-foreground line-clamp-2">
            {notification.message}
          </p>
          
          <div className="flex items-center justify-between mt-2">
            <p className="text-xs text-muted-foreground">
              {formatDistanceToNow(new Date(notification.created_at), { 
                addSuffix: true 
              })}
            </p>
            
            <ChevronRight className="h-3 w-3 text-muted-foreground" />
          </div>
        </div>
      </motion.div>
    );
  };

  return (
    <div className="flex flex-col h-full overflow-hidden">
      {/* Header */}
      <div className="p-4 border-b flex-shrink-0">
        {/* Mobile drag handle */}
        {isMobile && (
          <div className="w-12 h-1 bg-border rounded-full mx-auto mb-4" />
        )}
        <div className="flex items-center justify-between mb-3">
          <h2 className="text-lg font-semibold">Notifications</h2>
          <div className="flex items-center gap-2">
            {unreadCount > 0 && (
              <Button
                variant="ghost"
                size="sm"
                onClick={markAllAsRead}
                className="text-xs"
              >
                <Check className="h-3 w-3 mr-1" />
                Mark all read
              </Button>
            )}
            {isMobile && (
              <Button
                variant="ghost"
                size="icon"
                onClick={onClose}
                className="h-8 w-8"
              >
                <X className="h-4 w-4" />
              </Button>
            )}
          </div>
        </div>
        
        {/* Search */}
        <div className="relative">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
          <Input
            placeholder="Search notifications..."
            value={searchQuery}
            onChange={(e) => setSearchQuery(e.target.value)}
            className="pl-9"
          />
        </div>
      </div>
      
      {/* Categories */}
      <Tabs defaultValue="all" value={selectedCategory} onValueChange={setSelectedCategory} className="flex-1 flex flex-col overflow-hidden">
        <div className="border-b overflow-x-auto flex-shrink-0">
          <div className="flex min-w-max">
            <button
              onClick={() => setSelectedCategory('all')}
              className={cn(
                "flex-1 min-w-[60px] py-2.5 text-xs transition-colors relative border-b-2",
                selectedCategory === 'all' 
                  ? "border-primary text-primary font-medium" 
                  : "border-transparent hover:bg-accent/50"
              )}
            >
              All
              {unreadCounts.all > 0 && (
                <Badge variant="destructive" className="ml-1 h-4 min-w-[16px] px-1 text-[10px]">
                  {unreadCounts.all}
                </Badge>
              )}
            </button>
            <button
              onClick={() => setSelectedCategory('job')}
              className={cn(
                "flex-1 min-w-[60px] py-2.5 text-xs transition-colors relative border-b-2",
                selectedCategory === 'job' 
                  ? "border-primary text-primary font-medium" 
                  : "border-transparent hover:bg-accent/50"
              )}
            >
              Jobs
              {unreadCounts.job > 0 && (
                <Badge variant="destructive" className="ml-1 h-4 min-w-[16px] px-1 text-[10px]">
                  {unreadCounts.job}
                </Badge>
              )}
            </button>
            <button
              onClick={() => setSelectedCategory('booking')}
              className={cn(
                "flex-1 min-w-[70px] py-2.5 text-xs transition-colors relative border-b-2",
                selectedCategory === 'booking' 
                  ? "border-primary text-primary font-medium" 
                  : "border-transparent hover:bg-accent/50"
              )}
            >
              Bookings
              {unreadCounts.booking > 0 && (
                <Badge variant="destructive" className="ml-1 h-4 min-w-[16px] px-1 text-[10px]">
                  {unreadCounts.booking}
                </Badge>
              )}
            </button>
            <button
              onClick={() => setSelectedCategory('pitch')}
              className={cn(
                "flex-1 min-w-[65px] py-2.5 text-xs transition-colors relative border-b-2",
                selectedCategory === 'pitch' 
                  ? "border-primary text-primary font-medium" 
                  : "border-transparent hover:bg-accent/50"
              )}
            >
              Pitches
              {unreadCounts.pitch > 0 && (
                <Badge variant="destructive" className="ml-1 h-4 min-w-[16px] px-1 text-[10px]">
                  {unreadCounts.pitch}
                </Badge>
              )}
            </button>
            <button
              onClick={() => setSelectedCategory('chat')}
              className={cn(
                "flex-1 min-w-[60px] py-2.5 text-xs transition-colors relative border-b-2",
                selectedCategory === 'chat' 
                  ? "border-primary text-primary font-medium" 
                  : "border-transparent hover:bg-accent/50"
              )}
            >
              Chats
              {unreadCounts.chat > 0 && (
                <Badge variant="destructive" className="ml-1 h-4 min-w-[16px] px-1 text-[10px]">
                  {unreadCounts.chat}
                </Badge>
              )}
            </button>
            <button
              onClick={() => setSelectedCategory('payment')}
              className={cn(
                "flex-1 min-w-[75px] py-2.5 text-xs transition-colors relative border-b-2",
                selectedCategory === 'payment' 
                  ? "border-primary text-primary font-medium" 
                  : "border-transparent hover:bg-accent/50"
              )}
            >
              Payments
              {unreadCounts.payment > 0 && (
                <Badge variant="destructive" className="ml-1 h-4 min-w-[16px] px-1 text-[10px]">
                  {unreadCounts.payment}
                </Badge>
              )}
            </button>
          </div>
        </div>
        
        {/* Notification List */}
        <div className="flex-1 overflow-y-auto overflow-x-hidden">
            <motion.div 
              className="p-2 space-y-2 max-w-full"
              initial="hidden"
              animate="visible"
              variants={{
                visible: {
                  transition: {
                    staggerChildren: 0.05
                  }
                }
              }}
            >
              {isLoading ? (
                <div className="space-y-2">
                  {[...Array(3)].map((_, i) => (
                    <motion.div
                      key={i}
                      initial={{ opacity: 0, x: -20 }}
                      animate={{ opacity: 1, x: 0 }}
                      transition={{ delay: i * 0.1 }}
                      className="flex items-center gap-3 p-3"
                    >
                      <div className="h-10 w-10 rounded-full bg-muted animate-pulse" />
                      <div className="flex-1 space-y-2">
                        <div className="h-4 bg-muted rounded w-32 animate-pulse" />
                        <div className="h-3 bg-muted rounded w-48 animate-pulse" />
                      </div>
                    </motion.div>
                  ))}
                </div>
              ) : filteredNotifications.length === 0 ? (
                <div className="flex flex-col items-center justify-center h-32 text-muted-foreground">
                  <Inbox className="h-8 w-8 mb-2" />
                  <p className="text-sm">No notifications</p>
                </div>
              ) : (
                <AnimatePresence>
                  {filteredNotifications.map((notification) => (
                    <NotificationItem
                      key={notification.id}
                      notification={notification}
                    />
                  ))}
                </AnimatePresence>
              )}
              
              {hasMore && (
                <div className="p-3 text-center">
                  <Button
                    variant="ghost"
                    size="sm"
                    onClick={loadMore}
                    className="text-xs"
                  >
                    Load more
                  </Button>
                </div>
              )}
            </motion.div>
        </div>
      </Tabs>
      
      {/* Footer */}
      <div className="p-3 border-t bg-muted/50">
        <Button
          variant="ghost"
          size="sm"
          className="w-full justify-start hover:bg-accent/50 transition-colors"
          onClick={() => {
            onClose();
            navigate(`/${currentDashboard}/dashboard/account`, { state: { tab: 'notifications' } });
          }}
        >
          <Settings className="h-4 w-4 mr-2" />
          <span className="text-primary">Notification Settings</span>
        </Button>
      </div>
    </div>
  );
}